import TextInput from "../../TextInput";
import FormContent from "../../ADP/FormContent";
import NotificationBanner from "../../NotificationBanner";
import RadioGroup from "../../RadioGroup";
import Tooltip from "../../Tooltip";
import styles from "./index.module.scss";
import {
  dateNotInFutureFieldOptions,
  healthcardNumberFieldOptions,
  healthcardVersionCodeFieldOptions,
  phoneExtensionFieldOptions,
  phoneNumberFieldOptions,
  postalCodeFieldOptions,
  shortProvinceOptions,
} from "../../../utils/forms/validation";
import {
  typeOfBenefitOptions,
  relationshipOptions,
} from "../../../utils/labelMap/LabelMap";
import { useStoreContext } from "../../../contexts/StoreProvider";

const ClientForm = ({ benefits }: { benefits: boolean }) => {
  const {
    state: { externalUrls },
  } = useStoreContext();

  const sexOptions = [
    { label: "Female", value: "Female" },
    { label: "Male", value: "Male" },
  ];

  const cepOptions = [
    {
      label: "Yes",
      value: "true",
    },
    { label: "No", value: "false" },
  ];

  const benefitsOptions = [
    {
      label: "Yes",
      value: "true",
    },
    { label: "No", value: "false" },
  ];

  return (
    <form>
      <FormContent title="Client Personal Information">
        <NotificationBanner
          warning
          content="Please ensure all information entered in this section exactly matches what is registered with the Ontario Government, as seen on the client's health card."
          className={styles.matchNotice}
        />
        <div className={styles.grid}>
          <TextInput
            id="last_name"
            label="* Last Name"
            fieldOptions={{
              required: "This is a required field.",
            }}
            className={styles["col-1-6"]}
          />
          <TextInput
            id="first_name"
            label="* First Name"
            fieldOptions={{
              required: "This is a required field.",
            }}
            className={styles["col-6-11"]}
          />
          <TextInput
            id="middle_name"
            label="Middle Name"
            className={styles["col-11-15"]}
          />
          <TextInput
            id="birth_date"
            label="Date of Birth"
            placeholder="YYYY/MM/DD"
            className={styles["col-1-6"]}
            fieldOptions={dateNotInFutureFieldOptions}
          />
          <RadioGroup
            className={styles["col-1-15"]}
            name="sex"
            label="Sex"
            options={sexOptions}
            horizontal
          />
          <TextInput
            id="health_card"
            label="Healthcard Number"
            placeholder="0000-000-000"
            className={styles["col-1-7"]}
            fieldOptions={healthcardNumberFieldOptions}
          />
          <TextInput
            id="health_card_version"
            label="Version Code"
            placeholder="A or AA"
            className={styles["col-7-11"]}
            fieldOptions={{
              pattern: healthcardVersionCodeFieldOptions,
            }}
          />
          <TextInput
            id="long_term_care_home"
            label="Name of Long-Term Care Home (LTCH)"
            className={styles["col-1-11"]}
          />
          <RadioGroup
            className={styles["col-1-15"]}
            name="cep"
            label="Client is a CEP qualified client"
            options={cepOptions}
            horizontal
            tooltip={
              <Tooltip
                id="cep-client-profile-tooltip"
                content="The Central Equipment Pool (CEP) for High Technology Wheelchairs provides ADP Clients throughout Ontario with new and recycled high technology wheelchairs (those with power tilt and/or power wheelchairs as a component). For more information please see the ADP manual section 5."
                links={[
                  {
                    id: "link-1",
                    text: "ADP Manual",
                    href: externalUrls.mobility_devices_policy_and_administration_manual,
                  },
                ]}
              />
            }
          />
          {/* <RadioGroup
                    className={styles["col-1-15"]}
                    name="previous_cep"
                    label="Has ADP previously designated this client as CEP?"
                    options={cepOptions}
                    horizontal
                    tooltip={<Tooltip id="question" content="testing" />}
                  /> */}
          <RadioGroup
            className={styles["col-1-15"]}
            name="social_benefits"
            label="Client is recieving social assistance benefits"
            options={benefitsOptions}
            horizontal
          />
          {benefits && (
            <>
              <RadioGroup
                className={styles["col-1-15"]}
                name="social_assistance"
                description="Please check one:"
                options={typeOfBenefitOptions}
                requiredText="You selected that the client is receiving social assistance benefits, so please select a program below."
              />
              {/* <TextInput
                        id="benefits-ID"
                        label="Please enter social assistance benefits ID"
                        placeholder="Social Assistance Benefits ID number"
                        className={styles["col-1-11"]}
                      /> */}
            </>
          )}
        </div>

        <div className={styles.secondTitle}>Client Contact Information</div>
        <div className={styles.grid}>
          <TextInput
            section="address"
            id="phone"
            label="Client Phone Number"
            placeholder="000-000-0000"
            className={styles["col-1-7"]}
            fieldOptions={phoneNumberFieldOptions}
          />
          <TextInput
            section="address"
            id="unit_number"
            label="Unit Number"
            className={styles["col-1-3"]}
          />
          <TextInput
            section="address"
            id="street_number"
            label="Street Number"
            className={styles["col-3-6"]}
          />
          <TextInput
            section="address"
            id="street_name"
            label="Street Name"
            className={styles["col-6-12"]}
          />
          <TextInput
            section="address"
            id="lot"
            label="Lot/Concession/Rural Route"
            className={styles["col-1-12"]}
          />
          <TextInput
            section="address"
            id="city"
            label="City/Town"
            className={styles["col-1-7"]}
          />
          <TextInput
            section="address"
            id="province"
            label="Province"
            disabled
            placeholder="ON"
            className={styles["col-7-9"]}
          />
          <TextInput
            section="address"
            id="postal_code"
            label="Postal Code"
            className={styles["col-9-12"]}
            fieldOptions={postalCodeFieldOptions}
          />
          <TextInput
            section="address"
            id="business_phone"
            label="Business Telephone Number"
            placeholder="000-000-0000"
            fieldOptions={phoneNumberFieldOptions}
            className={styles["col-1-9"]}
          />
          <TextInput
            section="address"
            id="business_phone_ext"
            label="Extension"
            placeholder="000"
            fieldOptions={phoneExtensionFieldOptions}
            className={styles["col-9-12"]}
          />
        </div>
        <div className={styles.secondTitle}>
          Person who has signing authority (if not the client)
        </div>
        <div className={styles.grid}>
          <RadioGroup
            section="agent"
            name="relationship"
            description="Relationship to applicant"
            options={relationshipOptions}
            className={styles["col-1-7"]}
          />
          <TextInput
            section="agent"
            id="last_name"
            label="Last Name"
            className={styles["col-1-6"]}
          />
          <TextInput
            section="agent"
            id="first_name"
            label="First Name"
            className={styles["col-6-11"]}
          />
          <TextInput
            section="agent"
            id="middle_initial"
            label="Middle Initial"
            className={styles["col-11-15"]}
          />
          <TextInput
            section="agent"
            id="phone"
            label="Phone Number"
            placeholder="000-000-0000"
            className={styles["col-1-6"]}
            fieldOptions={phoneNumberFieldOptions}
          />
          <TextInput
            section="agent"
            id="unit_number"
            label="Unit Number"
            className={styles["col-1-3"]}
          />
          <TextInput
            section="agent"
            id="street_number"
            label="Street Number"
            className={styles["col-3-6"]}
          />
          <TextInput
            section="agent"
            id="street_name"
            label="Street Name"
            className={styles["col-6-12"]}
          />
          <TextInput
            section="agent"
            id="lot"
            label="Lot/Concession/Rural Route"
            placeholder=""
            className={styles["col-1-12"]}
          />
          <TextInput
            section="agent"
            id="city"
            label="City/Town"
            className={styles["col-1-7"]}
          />
          <TextInput
            section="agent"
            id="province"
            label="Province"
            maxLength={2}
            fieldOptions={shortProvinceOptions}
            className={styles["col-7-9"]}
          />
          <TextInput
            section="agent"
            id="postal_code"
            label="Postal Code"
            className={styles["col-9-12"]}
            fieldOptions={postalCodeFieldOptions}
          />
          <TextInput
            section="agent"
            id="business_phone"
            label="Business Telephone Number"
            placeholder="000-000-0000"
            className={styles["col-1-7"]}
            fieldOptions={phoneNumberFieldOptions}
          />
          <TextInput
            section="agent"
            id="business_phone_ext"
            label="Extension"
            placeholder="000"
            className={styles["col-7-12"]}
            fieldOptions={phoneExtensionFieldOptions}
          />
        </div>
      </FormContent>
    </form>
  );
};

export default ClientForm;
